import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { ApiUrl } from "../../../ApiUrl";

const AdmLessonPlanList = () => {
  const navigate = useNavigate();
  const [lessonPlans, setLessonPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const getPlanId = (plan) =>
    plan.id || plan.LESSON_PLAN_ID || plan.lesson_plan_id || plan.LessonPlanId || plan.Lesson_PLAN_ID;

  const getProfessorName = (plan) =>
    plan.employeeName || plan.professorName || plan.professor_name || plan.teacher_name || plan.teacherName || "-";

  const getLectureNo = (plan) =>
    plan.lectureno || plan.LectureNo || plan.lectureNo || plan.lecture_no || "-";

  const getModuleNo = (plan) =>
    plan.moduleno || plan.ModuleNo || plan.moduleNo || plan.module_no || "-";

  const getTopic = (plan) =>
    plan.topic_details || plan.topic || plan.Topic || plan.topics || "-";

  const getProposedDate = (plan) =>
    plan.proposedDate || plan.ProposedDate || plan.proposed_date || "-";

  const getTaughtDate = (plan) =>
    plan.TaughtDate || plan.taughtDate || plan.taught_date || "-";

  const getCompletion = (plan) =>
    plan.PercentageCompleted || plan.completion || plan.CompletionPercentage || plan.completion_percent || "-";

  const getRemarks = (plan) =>
    plan.remarks || plan.Remark || plan.remark || "-";

  const getDocumentValue = (plan) =>
    plan.document_name || plan.document || plan.Document || plan.file_name || plan.FileName || "-";

  const getDocumentUrl = (plan) =>
    plan.document_url || plan.documentUrl || plan.file_url || plan.fileUrl || plan.DocumentUrl || "";

  const loadLessonPlans = async () => {
    setLoading(true);
    try {
      const orgId = localStorage.getItem("orgId");
      const branchId = localStorage.getItem("branchId");
      const academicSessionId = localStorage.getItem("academicSessionId");

      if (!orgId || !branchId || !academicSessionId) {
        console.error("Missing required identifiers in localStorage");
        setLessonPlans([]);
        setFilteredPlans([]);
        return;
      }

      const response = await fetch(
        `${ApiUrl.apiurl}LESSION_PLAN/GetAllLessonPlan/?academic_year_id=${academicSessionId}&org_id=${orgId}&branch_id=${branchId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch lesson plans");
      }

      const data = await response.json();
      if (data && data.message === "success" && Array.isArray(data.data)) {
        setLessonPlans(data.data);
        setFilteredPlans(data.data);
      } else {
        setLessonPlans([]);
        setFilteredPlans([]);
      }
    } catch (error) {
      console.error("Error fetching lesson plans:", error);
      setLessonPlans([]);
      setFilteredPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLessonPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    const lowerValue = value.toLowerCase();

    if (!lowerValue.trim()) {
      setFilteredPlans(lessonPlans);
      return;
    }

    const filtered = lessonPlans.filter((plan) => {
      const searchableText = [
        getProfessorName(plan),
        getLectureNo(plan),
        getModuleNo(plan),
        getTopic(plan),
        getProposedDate(plan),
        getTaughtDate(plan),
        getCompletion(plan),
        getRemarks(plan),
        getDocumentValue(plan),
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(lowerValue);
    });

    setFilteredPlans(filtered);
  };

  const handleClear = async () => {
    setSearchText("");
    await loadLessonPlans();
  };

  const handleAddNew = () => {
    navigate("/admin/lesson-plan");
  };

  const handleEdit = (planId) => {
    if (!planId) return;
    navigate(`/admin/lesson-plan?id=${planId}`);
  };

  const handleDelete = async (planId) => {
    if (!planId) return;

    if (!window.confirm("Are you sure you want to delete this lesson plan?")) {
      return;
    }

    try {
      const response = await fetch(
        `${ApiUrl.apiurl}LESSION_PLAN/GetTeacherlessionPlanDelete/${planId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.ok) {
        alert("Lesson plan deleted successfully!");
        await loadLessonPlans();
      } else {
        alert("Failed to delete lesson plan.");
      }
    } catch (error) {
      console.error("Error deleting lesson plan:", error);
      alert("Error deleting lesson plan.");
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <div className="mb-4 text-center">
                <h4 className="mb-0">VIEW ALL LESSON PLANS</h4>
              </div>

              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-3">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleAddNew}
                  >
                    + Add New
                  </button>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12 col-md-6">
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search by Professor, Lecture, Module, Topic, Remarks..."
                    value={searchText}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th>Professor Name</th>
                      <th>Lecture No</th>
                      <th>Module No</th>
                      <th>Topic</th>
                      <th>Proposed Date</th>
                      <th>Taught Date</th>
                      <th>Completion %</th>
                      <th>Remarks</th>
                      <th>Document</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10} className="text-center py-4">
                          Loading lesson plans...
                        </td>
                      </tr>
                    ) : filteredPlans.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center py-4">
                          No lesson plans found
                        </td>
                      </tr>
                    ) : (
                      filteredPlans.map((plan) => {
                        const planId = getPlanId(plan);
                        const documentUrl = getDocumentUrl(plan);
                        const documentLabel = getDocumentValue(plan);

                        return (
                          <tr key={planId || Math.random()}>
                            <td>{getProfessorName(plan)}</td>
                            <td>{getLectureNo(plan)}</td>
                            <td>{getModuleNo(plan)}</td>
                            <td>{getTopic(plan)}</td>
                            <td>{getProposedDate(plan)}</td>
                            <td>{getTaughtDate(plan)}</td>
                            <td>{getCompletion(plan)}</td>
                            <td>{getRemarks(plan)}</td>
                            <td>
                              {documentUrl ? (
                                <a
                                  href={documentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View
                                </a>
                              ) : (
                                documentLabel || "-"
                              )}
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-warning btn-sm text-dark me-2"
                                onClick={() => handleEdit(planId)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDelete(planId)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmLessonPlanList;